import Button from '@mui/material/Button';
import React, { ElementType } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import ArrowSvg from '../../../../public/static/arrow.svg';

export const StyledButtonWrapper = styled.div`
    white-space: nowrap;
    display: inline-block;
`;

const stylePrimary = (props: ThemeProps) => css`
    background-color: ${props.theme.colors.main5};
    color: ${props.theme.shades.white};
    font:
        normal bold 16px/22px NotoSans,
        Helvetica,
        sans-serif;
    height: '45px';
    &:hover,
    &:focus {
        background-color: ${props.theme.colors.main7};
    }
`;
const styleSecondary = (props: ThemeProps) => css`
    background-color: ${props.theme.colors.main4};
    color: ${props.theme.shades.white};
    font:
        normal bold 16px/22px NotoSans,
        Helvetica,
        sans-serif;
    height: '45px';
    &:hover,
    &:focus {
        background-color: ${props.theme.colors.main8};
    }
`;
const styleTertiary = (props: ThemeProps) => css`
    border: 2px solid ${props.theme.colors.main5};
    color: ${props.theme.colors.main5};
    font:
        normal bold 14px/22px NotoSans,
        Helvetica,
        sans-serif;
    height: '40px';
`;

const styleOutlined = (props: ThemeProps) => css`
    border: 2px solid ${props.theme.colors.main5};
    color: ${props.theme.colors.main5};
    font:
        normal bold 14px/22px NotoSans,
        Helvetica,
        sans-serif;
    height: '40px';
    padding-left: ${props.theme.colors.main5};
    padding-right:
        normal bold 14px/22px NotoSans,
        Helvetica,
        sans-serif;
`;

const stylePrimaryRipple = (props: ThemeProps) => css`
    background-color: ${props.theme.colors.main7};
    background-image: radial-gradient(circle, transparent 1%, ${props.theme.colors.main7} 1%);
`;
const styleSecondaryRipple = (props: ThemeProps) => css`
    background-color: ${props.theme.colors.main8};
    background-image: radial-gradient(circle, transparent 1%, ${props.theme.colors.main8} 1%);
`;
const styleTertiaryRipple = (props: ThemeProps) => css`
    background-color: ${props.theme.dropShadows['black-8']};
    background-image: radial-gradient(
        circle,
        transparent 1%,
        ${props.theme.dropShadows['black-8']} 1%
    );
`;

const DLButton = ({ style, domType, as, ...rest }: IStyledButton) => {
    const component = as ?? 'button';
    return <Button style={style} component={component} {...rest} />;
};

interface IStyledButton extends ThemeProps {
    onClick: () => void;
    href: string;
    style: React.CSSProperties;
    btntype: 'primary' | 'secondary' | 'tertiary' | 'outlined';
    as: ElementType;
    domType: boolean;
}

export const StyledButton = styled(DLButton)`
    && {
        position: relative; /* Allows absolute position for the arrow */
        align-items: center;
        border-radius: 5px;
        display: flex;
        justify-content: center;
        letter-spacing: normal;
        line-height: 22px;
        text-transform: none;
        padding-right: 30px;

        span:first-child {
            padding-right: 25px;
            padding-left: 25px;
        }

        ${({ btntype }: IStyledButton) =>
            btntype === 'tertiary'
                ? styleTertiary
                : btntype === 'secondary'
                ? styleSecondary
                : btntype === 'outlined'
                ? styleOutlined
                : stylePrimary}

        /* Ripple effect for div */
        ${({ domType, ...props }: IStyledButton) => {
            if (!domType) return null;

            return css`
                padding-right: 25px;
                padding-left: 25px;

                background-position: center;
                transition: background 0.8s;

                &:hover {
                    cursor: pointer;
                    text-decoration: none;
                    background-position: center;
                    background-size: 15000%;

                    ${props.btntype === 'tertiary'
                        ? styleTertiaryRipple(props)
                        : props.btntype === 'secondary'
                        ? styleSecondaryRipple(props)
                        : stylePrimaryRipple(props)}
                }
                &:active {
                    text-decoration: none;
                    background-color: ${props.btntype === 'primary'
                        ? props.theme.colors.main5
                        : 'rgba(0, 0, 0, 0.08)'};
                    background-size: 100%;
                    transition: background 0s;
                }
            `;
        }}
    }
`;

export const StyledRightArrowIcon = styled(ArrowSvg)<{ size?: 'S' }>`
    position: absolute;
    right: 9px;
    fill: currentColor;
    && {
        width: ${(props) => (props.size === 'S' ? `10px` : `16px`)};
    }
`;
